import React, { useState, useEffect, lazy, Suspense } from 'react';
import ExifReader from 'exifreader';
import { clsx } from 'clsx';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import SafeIcon from './common/SafeIcon';
import DropZone from './components/DropZone';
import Footer from './components/Footer';
import Benefits from './components/Benefits';
import FAQ from './components/FAQ';
import SocialShare from './components/SocialShare';
import CompareTool from './components/CompareTool';
import { analyzeForensics } from './utils/forensics';

const ExifView = lazy(() => import('./components/ExifView'));
const ForensicsReport = lazy(() => import('./components/ForensicsReport'));

function App() {
  const [activeTab, setActiveTab] = useState('investigate'); // 'investigate' or 'compare'
  const [file, setFile] = useState(null);
  const [exifData, setExifData] = useState(null);
  const [forensicsData, setForensicsData] = useState(null);
  const [isForensicsMode, setIsForensicsMode] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileSelect = async (selectedFile) => {
    try {
      setError(null);
      setFile(selectedFile);
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      const tags = await ExifReader.load(selectedFile);
      setExifData(tags);
      const report = analyzeForensics(selectedFile, tags);
      setForensicsData(report);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      setError("Failed to parse image data.");
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-gray-900 font-sans">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 text-white p-2 rounded-lg shadow-sm">
              <SafeIcon icon={FiIcons.FiAperture} className="text-xl" />
            </div>
            <h1 className="text-xl font-bold tracking-tight">Exif<span className="text-blue-600">Investigator</span></h1>
          </div>
          
          <nav className="hidden md:flex items-center bg-gray-100 p-1 rounded-xl border border-gray-200">
            <button 
              onClick={() => setActiveTab('investigate')}
              className={clsx(
                "px-4 py-1.5 rounded-lg text-xs font-bold transition-all",
                activeTab === 'investigate' ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
              )}
            >
              INVESTIGATE
            </button>
            <button 
              onClick={() => setActiveTab('compare')}
              className={clsx(
                "px-4 py-1.5 rounded-lg text-xs font-bold transition-all",
                activeTab === 'compare' ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
              )}
            >
              COMPARE
            </button>
          </nav>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsForensicsMode(!isForensicsMode)}
              className={clsx(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                isForensicsMode ? "bg-red-600" : "bg-gray-200"
              )}
            >
              <span className={clsx(
                "inline-block h-4 w-4 transform rounded-full bg-white transition shadow-sm",
                isForensicsMode ? "translate-x-6" : "translate-x-1"
              )} />
            </button>
            <span className={clsx("text-[10px] font-black uppercase tracking-widest transition", isForensicsMode ? "text-red-600" : "text-gray-400")}>
              Forensics
            </span>
          </div>
        </div>
      </header>

      <main className="flex-grow max-w-6xl w-full mx-auto px-4 py-8">
        {activeTab === 'compare' ? (
          <div className="max-w-5xl mx-auto">
             <div className="mb-10 text-center">
                <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Image Contrast Suite</h2>
                <p className="text-gray-500">Compare two versions of an image to detect metadata tampering or subtle visual edits.</p>
             </div>
             <CompareTool />
          </div>
        ) : (
          !file ? (
            <div className="max-w-5xl mx-auto">
              <div className="max-w-xl mx-auto mt-12 mb-20 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-6 font-bold uppercase tracking-[0.15em] text-blue-700 text-[10px]">
                  <SafeIcon icon={FiIcons.FiShield} /> takedowntaskforce.com
                </div>
                <h2 className="text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">Professional Image Forensics</h2>
                <p className="text-lg text-gray-500 mb-10">Audit metadata, detect manipulations, and verify authenticity locally.</p>
                <DropZone onFileSelect={handleFileSelect} />
              </div>
              <Benefits />
              <FAQ />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4 space-y-6">
                <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                  <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden mb-5 border border-gray-100">
                    <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
                  </div>
                  
                  <div className="space-y-4">
                    <div className="border-b border-gray-100 pb-3">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Veracity Score</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className={clsx(
                          "text-2xl font-black",
                          forensicsData?.scores.veracity > 80 ? "text-green-600" : 
                          forensicsData?.scores.veracity > 50 ? "text-orange-500" : "text-red-600"
                        )}>
                          {forensicsData?.scores.veracity}%
                        </span>
                        <span className="text-xs font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600 uppercase">
                          {forensicsData?.scores.label}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
                        <div 
                          className={clsx(
                            "h-full transition-all duration-1000",
                            forensicsData?.scores.veracity > 80 ? "bg-green-500" : 
                            forensicsData?.scores.veracity > 50 ? "bg-orange-500" : "bg-red-500"
                          )}
                          style={{ width: `${forensicsData?.scores.veracity}%` }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Size</p>
                        <p className="text-sm font-semibold text-gray-900">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Format</p>
                        <p className="text-sm font-semibold text-gray-900 uppercase">{file.type.split('/')[1]}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Primary Platform</p>
                        <p className="text-sm font-semibold text-blue-600 flex items-center gap-1">
                          <SafeIcon icon={FiIcons.FiGlobe} className="text-xs" />
                          {forensicsData?.platform || 'Unknown'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Timestamp</p>
                        <p className="text-[11px] font-semibold text-gray-900 leading-tight">
                          {forensicsData?.timestamps.original ? format(forensicsData.timestamps.original, 'yyyy-MM-dd HH:mm') : format(file.lastModified, 'yyyy-MM-dd HH:mm')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <SocialShare fileName={file.name} veracity={forensicsData?.scores.veracity} />

                  <button 
                    onClick={() => { setFile(null); setExifData(null); setForensicsData(null); setPreviewUrl(null); }}
                    className="w-full mt-6 flex items-center justify-center gap-2 text-red-600 bg-red-50 hover:bg-red-100 py-3 rounded-xl transition font-bold text-sm"
                  >
                    <SafeIcon icon={FiIcons.FiRefreshCw} /> New Analysis
                  </button>
                </div>
              </div>

              <div className="lg:col-span-8">
                {error ? (
                  <div className="bg-red-50 text-red-700 p-6 rounded-xl border border-red-200 flex items-center gap-3">
                    <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-2xl" /> {error}
                  </div>
                ) : (
                  <Suspense fallback={<div className="flex flex-col items-center justify-center h-64 bg-white rounded-2xl border border-gray-100"><SafeIcon icon={FiIcons.FiLoader} className="text-4xl text-blue-500 animate-spin mb-4" /><p className="text-gray-500 font-medium">Processing...</p></div>}>
                    {isForensicsMode ? (
                       forensicsData && <ForensicsReport report={forensicsData} previewUrl={previewUrl} fileName={file.name} />
                    ) : (
                       exifData && <ExifView tags={exifData} />
                    )}
                  </Suspense>
                )}
              </div>
            </div>
          )
        )}
      </main>
      <Footer />
    </div>
  );
}

export default App;